import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, Download, Upload, AlertTriangle, CheckCircle2, Loader2,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail, API } from "../lib/api";

/**
 * AdminBackup — full-collection export + restore.
 *
 * Use case: moving LAST between hosting providers (Emergent → Railway →
 * Atlas). One click dumps every relevant Mongo collection to a JSON file
 * the admin downloads; another click takes that JSON and restores it on
 * a fresh environment.
 *
 * Restore is destructive (collections are dropped before reinsert) so
 * the page requires an explicit "I understand" confirmation before it
 * fires the upload.
 */
export default function AdminBackup() {
  const { user, logout } = useAuth();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null); // { restored, skipped, source_exported_at }
  const [confirm, setConfirm] = useState(false);
  const fileRef = useRef(null);

  const handleExport = async () => {
    setError("");
    setExporting(true);
    try {
      // We want the raw JSON Blob as a file download — fall back to a fresh
      // fetch so axios doesn't try to parse it. Same auth cookies are sent
      // by default.
      const res = await fetch(`${API}/admin/backup/export`, {
        method: "GET",
        credentials: "include",
      });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Export failed (${res.status}). ${txt.slice(0, 200)}`);
      }
      const blob = await res.blob();
      // Figure out a tidy filename — server sends Content-Disposition but
      // some hosts strip it. Fall back to a timestamped default.
      const cd = res.headers.get("Content-Disposition") || "";
      const match = cd.match(/filename="([^"]+)"/);
      const filename =
        (match && match[1]) ||
        `last-backup-${new Date().toISOString().replace(/[:.]/g, "-")}.json`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e.message || "Couldn't export.");
    } finally {
      setExporting(false);
    }
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!confirm) {
      setError("Tick the confirmation box first — restore is destructive.");
      event.target.value = "";
      return;
    }
    setError("");
    setResult(null);
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api.post("/admin/backup/import", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
    } catch (e) {
      setError(formatApiErrorDetail(e) || "Restore failed.");
    } finally {
      setImporting(false);
      event.target.value = "";
    }
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-white text-[var(--brand-ink)]"
      data-testid="admin-backup"
    >
      <header className="border-b border-black/10">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-5 sm:px-8">
          <Link
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-bold hover:text-[#6CABDD]"
          >
            <ArrowLeft size={14} /> Back to admin
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden text-xs text-[var(--brand-body)] sm:inline">
              {user?.email}
            </span>
            <button
              type="button"
              onClick={logout}
              className="rounded-md border border-black/10 px-3 py-2 font-display text-xs font-semibold hover:border-[#0A0F17]"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 py-12 sm:px-8">
        <p className="label-kicker text-brand-accent">Backup &amp; Restore</p>
        <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
          Move LAST anywhere.
        </h1>
        <p className="mt-3 max-w-xl text-sm leading-relaxed text-[var(--brand-body)]">
          Dump every post, page, setting, theme tweak, social-wall item and nav
          item to a single JSON file. Restore that file on a fresh database
          (Railway, MongoDB Atlas, anywhere) to clone your site one-for-one.
        </p>

        {error ? (
          <p
            data-testid="backup-error"
            className="mt-6 rounded-md border-l-2 border-[#E63946] bg-[#E63946]/5 px-4 py-3 text-sm"
          >
            {error}
          </p>
        ) : null}

        {/* EXPORT */}
        <section className="mt-10 rounded-md border border-black/10 bg-[#F8FAFC] p-6">
          <h2 className="font-display text-xl font-black tracking-tight">
            Step 1 — Download a backup
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-[var(--brand-body)]">
            Saves a single JSON file to your computer. Run it from the
            environment whose content you want to copy (probably your live
            site). Skips login attempt logs and nav-click telemetry —
            everything else (posts, settings, theme, nav, custom pages,
            social wall, contact messages, users including bcrypt-hashed
            admin password) is included.
          </p>
          <button
            type="button"
            onClick={handleExport}
            disabled={exporting}
            data-testid="backup-export-button"
            className="mt-5 inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-5 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-[#6CABDD] hover:text-[#0A0F17] disabled:opacity-50"
          >
            {exporting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Exporting…
              </>
            ) : (
              <>
                <Download size={14} strokeWidth={2.5} /> Download backup
              </>
            )}
          </button>
        </section>

        {/* IMPORT */}
        <section className="mt-8 rounded-md border-2 border-[#E63946]/30 bg-[#FEF6F7] p-6">
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} className="mt-0.5 shrink-0 text-[#E63946]" />
            <div>
              <h2 className="font-display text-xl font-black tracking-tight">
                Step 2 — Restore a backup (destructive)
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-[var(--brand-body)]">
                Uploads a backup file and{" "}
                <strong className="text-[#0A0F17]">overwrites every
                collection</strong>{" "}
                in this environment's database with the file's contents.
                Designed to be run on a fresh empty database after switching
                hosts. Do <strong className="text-[#0A0F17]">not</strong> run
                it on a live production site — you'll lose any data created
                since the backup was taken.
              </p>
              <p className="mt-2 text-xs text-[var(--brand-body)]">
                Uploaded files (cover images, gallery photos) are{" "}
                <strong className="text-[#0A0F17]">not</strong> included in
                the backup. Re-upload covers via the post editor after
                restore, or rsync the <code>/app/backend/uploads</code>{" "}
                folder if you have shell access.
              </p>
            </div>
          </div>

          <label className="mt-5 flex cursor-pointer items-start gap-3 rounded-md border border-black/10 bg-white p-3">
            <input
              type="checkbox"
              checked={confirm}
              onChange={(e) => setConfirm(e.target.checked)}
              data-testid="backup-import-confirm"
              className="mt-1 h-4 w-4"
            />
            <span className="text-sm text-[var(--brand-ink)]">
              I understand this will <strong>erase</strong> the current
              database in this environment and replace it with the contents
              of the uploaded file.
            </span>
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={!confirm || importing}
              data-testid="backup-import-button"
              className="inline-flex items-center gap-2 rounded-md bg-[#E63946] px-5 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-[#0A0F17] disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importing ? (
                <>
                  <Loader2 size={14} className="animate-spin" /> Restoring…
                </>
              ) : (
                <>
                  <Upload size={14} strokeWidth={2.5} /> Choose backup file…
                </>
              )}
            </button>
            <input
              ref={fileRef}
              type="file"
              accept="application/json,.json"
              onChange={handleFileChange}
              data-testid="backup-import-file"
              className="hidden"
            />
            <span className="text-xs text-[var(--brand-body)]">
              Expects a `.json` file from this page's "Download backup"
              button.
            </span>
          </div>
        </section>

        {/* RESULT */}
        {result ? (
          <section
            data-testid="backup-import-result"
            className="mt-8 rounded-md border border-[#16A34A]/30 bg-[#16A34A]/5 p-6"
          >
            <div className="flex items-start gap-3">
              <CheckCircle2 size={20} className="mt-0.5 shrink-0 text-[#16A34A]" />
              <div>
                <h2 className="font-display text-lg font-black tracking-tight">
                  Restore complete.
                </h2>
                <p className="mt-1 text-xs text-[var(--brand-body)]">
                  Backup from{" "}
                  {result.source_exported_at
                    ? new Date(result.source_exported_at).toLocaleString("en-GB")
                    : "an unknown time"}{" "}
                  · finished{" "}
                  {result.imported_at
                    ? new Date(result.imported_at).toLocaleTimeString("en-GB")
                    : "now"}
                  .
                </p>
                <ul className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {Object.entries(result.restored || {}).map(([k, v]) => (
                    <li key={k} className="flex justify-between gap-3">
                      <span className="text-[var(--brand-body)]">{k}</span>
                      <span className="font-mono font-bold">{v}</span>
                    </li>
                  ))}
                </ul>
                {result.skipped && result.skipped.length ? (
                  <p className="mt-4 text-xs text-[var(--brand-body)]">
                    Skipped: {result.skipped.join(", ")}
                  </p>
                ) : null}
                <p className="mt-4 text-xs text-[var(--brand-body)]">
                  Sign out and back in so the admin session reloads against
                  the restored user record.
                </p>
              </div>
            </div>
          </section>
        ) : null}
      </main>
    </div>
  );
}
